'use client';

import { type CSSProperties } from 'react';
import * as RSwitch from '@radix-ui/react-switch';
import { cn } from '../../lib/cn';

export interface SSwitchProps {
  /** on/off 상태 */
  value?: boolean;
  /** 스위치 좌측 라벨 */
  leftLabel?: string;
  /** 스위치 우측 라벨 */
  rightLabel?: string;
  /** @deprecated `rightLabel` 사용. 우측 라벨(하위호환) */
  label?: string;
  /** 비활성 */
  disabled?: boolean;
  /** 변경 (sdUpdate) */
  onValueChange?: (value: boolean) => void;
  className?: string;
  style?: CSSProperties;
}

/** SSwitch — sd-switch 포팅 (Radix SSwitch). 32×20 트랙, 16 노브. 좌/우 라벨 지원. */
export function SSwitch({
  value = false,
  leftLabel,
  rightLabel,
  label,
  disabled = false,
  onValueChange,
  className,
  style,
}: SSwitchProps) {
  // label(deprecated)은 rightLabel 로 흡수
  const right = rightLabel ?? label;
  // 원본 sd-switch: 라벨에 색 토큰 없음 → 색은 상속(별도 지정 안 함)
  const renderLabel = (text?: string) =>
    text ? <span className="text-[12px] font-medium leading-[20px]">{text}</span> : null;
  const trackBg = disabled
    ? value
      ? 'var(--cmp-switch-bg-disabled-on)'
      : 'var(--cmp-switch-bg-disabled-off)'
    : value
      ? 'var(--cmp-switch-bg-on)'
      : 'var(--cmp-switch-bg-off)';

  return (
    <label
      className={cn(
        'inline-flex items-center gap-[6px]',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        className,
      )}
      style={style}
    >
      {renderLabel(leftLabel)}
      <RSwitch.Root
        checked={value}
        disabled={disabled}
        onCheckedChange={v => onValueChange?.(v)}
        className={cn(
          'relative h-[20px] w-[32px] flex-shrink-0 rounded-full p-[2px] outline-none transition-colors duration-200',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        )}
        style={{ background: trackBg }}
      >
        <RSwitch.Thumb
          className="block h-[16px] w-[16px] rounded-full shadow-[0_2px_4px_rgba(0,0,0,0.16)] transition-transform duration-200 data-[state=checked]:translate-x-[12px]"
          style={{
            background: disabled
              ? 'var(--cmp-switch-knob-disabled)'
              : 'var(--cmp-switch-knob-default)',
          }}
        />
      </RSwitch.Root>
      {renderLabel(right)}
    </label>
  );
}
